<template>
  <section>
    <div class="tw-mb-4 tw-flex tw-items-center tw-justify-between">
      <div class="tw-flex tw-items-center tw-gap-2">
        <StickyNote class="tw-h-4 tw-w-4 tw-text-secondary-500 dark:tw-text-neutral-400" />
        <h2
          class="tw-font-heading tw-text-sm tw-font-semibold tw-uppercase tw-tracking-wide tw-text-secondary-700 dark:tw-text-neutral-300"
        >
          {{ t('orders.titles.self_notes') }}
        </h2>
      </div>
      <Button variant="outline" size="sm" @click="openAddDialog">
        <Plus class="tw-h-4 tw-w-4" />
        {{ t('orders.titles.add_note') }}
      </Button>
    </div>

    <!-- Loading State -->
    <div v-if="isLoading" class="tw-space-y-3">
      <div
        v-for="i in 2"
        :key="i"
        class="tw-rounded-xl tw-border tw-border-surface-200 tw-p-4 dark:tw-border-neutral-800"
      >
        <div class="tw-flex tw-items-center tw-justify-between tw-mb-3">
          <div class="tw-flex tw-items-center tw-gap-2">
            <Skeleton class="tw-h-9 tw-w-9 tw-rounded-full" />
            <div>
              <Skeleton class="tw-h-4 tw-w-24 tw-mb-1" />
              <Skeleton class="tw-h-3 tw-w-16" />
            </div>
          </div>
        </div>
        <Skeleton class="tw-h-14 tw-w-full tw-rounded-lg" />
      </div>
    </div>

    <!-- Notes List -->
    <div v-else-if="notes.length" class="tw-space-y-3">
      <article
        v-for="note in notes"
        :key="note.id"
        class="tw-group tw-rounded-xl tw-border tw-border-surface-200 tw-bg-white tw-p-4 tw-transition-colors hover:tw-border-surface-300 dark:tw-border-neutral-800 dark:tw-bg-neutral-900/40"
      >
        <div class="tw-flex tw-items-start tw-justify-between tw-mb-3">
          <div class="tw-flex tw-items-center tw-gap-3">
            <Avatar size="sm" shape="circle">
              <AvatarImage
                v-if="note.author?.profile_picture"
                :src="note.author.profile_picture"
                :alt="note.author?.display_name"
              />
              <AvatarFallback
                class="tw-bg-primary-100 tw-text-xs tw-font-semibold tw-text-primary-800 dark:tw-bg-primary-900/40 dark:tw-text-primary-200"
              >
                {{ getInitials(note.author?.display_name) }}
              </AvatarFallback>
            </Avatar>
            <div>
              <p class="tw-text-sm tw-font-semibold tw-text-secondary-900 dark:tw-text-neutral-100">
                {{ note.author?.display_name }}
              </p>
              <p class="tw-text-xs tw-text-surface-500 dark:tw-text-neutral-400">
                {{ moment(note.created_on).format('DD.MM.YYYY') }}
              </p>
            </div>
          </div>
          <div
            class="tw-flex tw-gap-0.5 tw-opacity-0 tw-transition-opacity group-hover:tw-opacity-100"
          >
            <Button
              variant="ghost"
              size="icon"
              class="tw-h-8 tw-w-8 tw-text-surface-500 hover:tw-text-secondary-900"
              @click="openEditDialog(note)"
            >
              <Pencil class="tw-h-4 tw-w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              class="tw-h-8 tw-w-8 tw-text-surface-500 hover:tw-text-red-600 hover:tw-bg-red-50 dark:hover:tw-bg-red-950/30"
              @click="openDeleteDialog(note)"
            >
              <Trash2 class="tw-h-4 tw-w-4" />
            </Button>
          </div>
        </div>
        <p
          class="tw-text-sm tw-text-secondary-700 tw-whitespace-pre-wrap tw-leading-relaxed dark:tw-text-neutral-300"
        >
          {{ note.text }}
        </p>
      </article>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-rounded-xl tw-border tw-border-dashed tw-border-surface-200 tw-py-10 dark:tw-border-neutral-800"
    >
      <StickyNote class="tw-h-11 tw-w-11 tw-text-surface-300 dark:tw-text-neutral-600" />
      <p class="tw-mt-3 tw-text-sm tw-font-medium tw-text-surface-500 dark:tw-text-neutral-400">
        {{ t('orders.titles.no_self_notes') }}
      </p>
    </div>

    <!-- Add/Edit Dialog -->
    <Dialog v-model:open="isNoteDialogOpen">
      <DialogContent class="sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle>
            {{ isEditMode ? t('orders.titles.edit_note') : t('orders.titles.add_self_note') }}
          </DialogTitle>
          <DialogDescription>
            {{ t('orders.titles.self_note_dialog_description') }}
          </DialogDescription>
        </DialogHeader>
        <form @submit.prevent="handleSubmit">
          <Textarea
            v-model="noteText"
            :placeholder="t('orders.titles.self_note_hint')"
            class="tw-min-h-[150px]"
          />
          <DialogFooter class="tw-mt-4">
            <Button type="button" variant="outline" @click="isNoteDialogOpen = false">
              {{ t('common.forms.cancel') }}
            </Button>
            <Button type="submit" :disabled="!noteText.trim()">
              {{ isEditMode ? t('orders.titles.edit_note') : t('orders.titles.add_note') }}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>

    <!-- Delete Confirmation Dialog -->
    <Dialog v-model:open="isDeleteDialogOpen">
      <DialogContent class="sm:tw-max-w-md">
        <DialogHeader>
          <DialogTitle>{{ t('common.forms.delete') }}</DialogTitle>
          <DialogDescription>{{ t('orders.titles.confirm_delete') }}?</DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" @click="isDeleteDialogOpen = false">
            {{ t('common.forms.cancel') }}
          </Button>
          <Button variant="destructive" @click="deleteNote">
            {{ t('common.forms.delete') }}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, watch } from 'vue';
import moment from 'moment';
import { useI18n } from 'vue-i18n';
import { usePaymentStore } from '@/stores/payment.store';
import { useApiUtils } from '@/_helpers/composables/api-utils.composable';

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Plus, Pencil, Trash2, StickyNote } from 'lucide-vue-next';

const { t } = useI18n();
const { handleApiError } = useApiUtils();
const { getPaymentNotes, createPaymentNotes, editPaymentNotes, deletePaymentNotes } =
  usePaymentStore();

const props = defineProps<{
  paymentId: number | null;
}>();

const notes = ref<any[]>([]);
const isLoading = ref(true);
const isNoteDialogOpen = ref(false);
const isDeleteDialogOpen = ref(false);
const isEditMode = ref(false);
const noteText = ref('');
const selectedNoteId = ref<number | null>(null);

const getInitials = (name: string | undefined) => {
  if (!name) return 'U';
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const loadNotes = async () => {
  if (!props.paymentId) return;
  isLoading.value = true;
  const response = await getPaymentNotes(props.paymentId);
  notes.value = response.results || [];
  isLoading.value = false;
};

const openAddDialog = () => {
  isEditMode.value = false;
  noteText.value = '';
  selectedNoteId.value = null;
  isNoteDialogOpen.value = true;
};

const openEditDialog = (note: any) => {
  isEditMode.value = true;
  noteText.value = note.text || note.note || '';
  selectedNoteId.value = note.id;
  isNoteDialogOpen.value = true;
};

const openDeleteDialog = (note: any) => {
  selectedNoteId.value = note.id;
  isDeleteDialogOpen.value = true;
};

const handleSubmit = async () => {
  if (!noteText.value.trim() || !props.paymentId) return;

  if (isEditMode.value && selectedNoteId.value) {
    await handleApiError(async () => {
      await editPaymentNotes(selectedNoteId.value!, {
        payment: props.paymentId,
        text: noteText.value,
      });
      await loadNotes();
    }, t('requests.titles.successful_self_note_edited'));
  } else {
    await handleApiError(async () => {
      await createPaymentNotes({
        payment: props.paymentId,
        text: noteText.value,
      });
      await loadNotes();
    }, t('requests.titles.successful_self_note_created'));
  }

  isNoteDialogOpen.value = false;
  noteText.value = '';
};

const deleteNote = async () => {
  if (!selectedNoteId.value) return;

  await handleApiError(async () => {
    await deletePaymentNotes(selectedNoteId.value!);
    await loadNotes();
  }, t('requests.titles.successful_self_note_deleted'));

  isDeleteDialogOpen.value = false;
  selectedNoteId.value = null;
};

onMounted(loadNotes);

watch(() => props.paymentId, loadNotes);
</script>
